import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from '@workos-inc/authkit-react';

import AccountIcon from '../../components/AccountIcon';
import { TOGGLE_BAND } from '../../components/viewToggleMetrics';
import { ApiError } from '../../shared/api';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { usePhoneChrome } from '../../shared/usePhoneChrome';
import { useWeek } from '../../shared/weekContext';
import ClearScheduleDialog from '../schedule/ClearScheduleDialog';
import { isWarningStat, WEEK_STAT_ROWS } from '../schedule/weekStats';
import CalendarSyncDialog from '../share/CalendarSyncDialog';
import { DIALOG_BG } from '../share/dialogParts';
import ShareLinkDialog from '../share/ShareLinkDialog';
import BackdropHeader from './BackdropHeader';
import BackdropPage from './BackdropPage';
import { Card, Chip, Emoji } from './accountParts';
import { CalendarSyncGlyph, LinkGlyph } from './accountGlyphs';
import { CARD_INK, PANEL_BG } from './backdrop';
import type { OnboardingAnswers } from './accountApi';
import { flushBeforeSignOut } from './beforeSignOut';
import { PRIVACY_URL, TERMS_URL } from './legalLinks';
import { useMe } from './meContext';
import {
  COPY,
  DESCRIPTORS,
  optionFor,
  ORIGINS,
  REASONS,
  SOMETHING_ELSE,
  type Option,
} from './onboardingCopy';
import { forgetOrigin, recallOrigin } from './originStore';
import { ACCOUNT_PATHS, ACCOUNT_ROOT } from './paths';

/**
 * The Account page (D3), rebuilt on Sept 19 against Sam's two mockups.
 *
 * A full-bleed photograph with cards free-positioned on it rather than the
 * 420px form column it used to be. The first card's top is pinned to the
 * background's anchor line and everything else follows it down — that rule is
 * absolute, and it is why `--first-top` comes from `BackdropPage` rather than
 * from a margin here.
 *
 * Which of the two layouts is drawn follows `usePhoneChrome()` — the device,
 * not the width (Sam, Sept 19) — while the *background* is picked by width. A
 * narrow desktop window therefore gets the phone's picture behind the laptop's
 * layout, which is deliberate; the laptop layout stacks to one column there.
 */

/* ── Parts ──────────────────────────────────────────────────────────────── */

/** 28px of 375, and 461px of 1728: the same margin as the mockups' cards. */
const PHONE_INSET = '7.4667vw';

const Column = styled('div')({
  paddingTop: 'var(--first-top, 33vh)',
});

const PhoneColumn = styled(Column)({
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  padding: `var(--first-top, 33vh) ${PHONE_INSET} 24px`,
});

/**
 * The laptop's two columns over the grass, centred as the mockup places them:
 * the card at 26.7vw, 27.6vw wide, then 1.4vw of grass, then a 17vw stack.
 * In `vw` throughout, because the photograph they sit on scales with the
 * viewport's width and they have to stay on the same patch of it.
 */
const DeskColumns = styled(Column)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '27.6vw 16.96vw',
  justifyContent: 'center',
  columnGap: '1.39vw',
  alignItems: 'start',
  paddingBottom: 28,
  // A desktop window this narrow keeps the laptop layout (it is a device rule,
  // not a width one), but two columns of 27vw are two columns of nothing.
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'min(420px, 86vw)',
    justifyContent: 'center',
    rowGap: 16,
  },
}));

const Identity = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const Name = styled('div')({
  minWidth: 0,
  '& strong': {
    display: 'block',
    fontFamily: FONTS.display,
    fontWeight: 800,
    lineHeight: 1.15,
    overflowWrap: 'anywhere',
  },
  '& span': {
    display: 'block',
    fontFamily: FONTS.body,
    opacity: 0.85,
    overflowWrap: 'anywhere',
  },
});

/**
 * One answer group. The whole row is the control — no pencil, no "Edit" link —
 * so it is a button with a name that says which question it reopens.
 */
const Group = styled('button')({
  appearance: 'none',
  display: 'block',
  width: 'calc(100% + 16px)',
  margin: '0 -8px',
  padding: '6px 8px',
  border: '1.5px solid transparent',
  borderRadius: 14,
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  color: 'inherit',
  transition: 'background 120ms ease, border-color 120ms ease',
  '&:hover': { background: 'rgba(255,255,255,0.35)' },
  '&:focus-visible': { outline: 'none', borderColor: CARD_INK },
  // The air between one question and the next, as the mockups space them.
  '& + &': { marginTop: 8 },
  '[data-desk] & + &': { marginTop: 20 },
});

const Lead = styled('span')({
  display: 'block',
  marginBottom: 6,
  '[data-desk] &': { marginBottom: 8 },
  fontFamily: FONTS.display,
  fontWeight: 700,
});

const Chips = styled('span')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 11,
});

/** No answer yet: an invitation, not a gap. */
const AddAnswer = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  minHeight: 27,
  padding: '3px 14px',
  borderRadius: 999,
  border: `1.5px dashed rgba(54,47,42,0.45)`,
  fontFamily: FONTS.display,
  fontWeight: 700,
  opacity: 0.75,
});

const pill = {
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  boxSizing: 'border-box',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.display,
  fontWeight: 700,
  background: 'rgba(243, 238, 231, 0.80)',
  color: CARD_INK,
  textAlign: 'left',
  transition: 'background 120ms ease',
  '&:hover': { background: 'rgba(243, 238, 231, 0.94)' },
  '&:disabled': { opacity: 0.55, cursor: 'default' },
} as const;

const DeskAction = styled('button')({
  ...pill,
  minHeight: 48,
  padding: '0 20px',
  fontSize: 16,
});

const PhoneAction = styled('button')({
  ...pill,
  minHeight: 41,
  padding: '0 14px',
  fontSize: 13,
  lineHeight: 1.15,
});

/** Sign out and Delete my account: words on the grass, not buttons. */
const PlainAction = styled('button')({
  appearance: 'none',
  display: 'block',
  padding: '4px 0',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 15.5,
  color: BRAND.parchment,
  textAlign: 'left',
  '&:hover': { opacity: 0.75 },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
});

/** The phone's three buttons, which the mockup leaves below its fold. */
const WideAction = styled('button')({
  appearance: 'none',
  width: '100%',
  minHeight: 46,
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 15,
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.5)',
  transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease',
  '&:hover:not(:disabled)': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.1)' },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
  '&[data-danger="true"]:hover:not(:disabled)': { borderColor: '#ef3d23', color: '#ef3d23', background: 'none' },
});

const Stats = styled('div')({
  boxSizing: 'border-box',
  background: PANEL_BG,
  color: BRAND.parchment,
  borderRadius: 22,
  padding: '18px 20px 20px',
});

const StatsTitle = styled('h2')({
  margin: '0 0 12px',
  fontFamily: FONTS.display,
  fontWeight: 800,
  fontSize: 21,
});

const StatRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  padding: '5px 0',
  fontFamily: FONTS.body,
  fontSize: 15,
  '& strong': { fontWeight: 700 },
});

const Trouble = styled('p')({
  margin: 0,
  padding: '10px 14px',
  borderRadius: 12,
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.45,
  color: BRAND.parchment,
  background: 'rgba(0,0,0,0.35)',
  '& a': { color: 'inherit' },
});

/**
 * The two legal documents and the copyright, at 12px: the smallest size the
 * site sets running text in that still reads on the photograph. The links open
 * a new tab, as they do from the sign-up card, since the legal pages have no
 * way back to this one.
 */
const Legal = styled('footer')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.4,
  color: BRAND.parchment,
  textAlign: 'center',
  textShadow: '0 1px 3px rgba(0,0,0,0.45)',
  '& a': { color: 'inherit', textDecoration: 'none' },
  '& a:hover': { textDecoration: 'underline' },
});

const LegalLinks = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 22,
});

/** The dot between the links, drawn rather than typed so every font shows it. */
const LegalDot = styled('span')({
  width: 4,
  height: 4,
  borderRadius: '50%',
  background: 'currentColor',
});

function LegalFooter() {
  return (
    <Legal>
      <LegalLinks>
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
          Terms of Use
        </a>
        <LegalDot aria-hidden="true" />
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
      </LegalLinks>
      <span>© 2026 CommonGround. All rights reserved.</span>
    </Legal>
  );
}

/* ── Answers ────────────────────────────────────────────────────────────── */

type Step = 'descriptors' | 'origin' | 'reasons';

const EDIT_LABEL: Record<Step, string> = {
  descriptors: 'Edit how I describe myself',
  origin: 'Edit where I’m based',
  reasons: 'Edit what I want from Climate Week',
};

/** The words for a stored answer, with "Something else…" showing what was written. */
function chipsFor(keys: string[], options: Option[], other: string | null) {
  return keys
    .map((key) => ({ key, option: optionFor(options, key) }))
    .filter((row): row is { key: string; option: Option } => row.option !== null)
    .map(({ key, option }) => ({
      key,
      emoji: option.emoji,
      label: key === SOMETHING_ELSE && other?.trim() ? other.trim() : option.label,
    }));
}

/* ── The page ───────────────────────────────────────────────────────────── */

export default function AccountPage() {
  const { user, signOut } = useAuth();
  const { status, api } = useMe();
  const navigate = useNavigate();
  const phone = usePhoneChrome();
  const week = useWeek();

  const [answers, setAnswers] = useState<OnboardingAnswers | null>(null);
  const [dialog, setDialog] = useState<'link' | 'calendar' | 'clear' | 'delete' | 'unsaved' | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);

  const ready = status.kind === 'ready';
  const userId = ready ? status.me.id : '';

  // The user's own answers, for the three groups.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    api
      .onboarding()
      .then((found) => {
        if (!cancelled) setAnswers(found);
      })
      .catch(() => {
        // The groups simply don't draw; the rest of the page still works.
      });
    return () => {
      cancelled = true;
    };
  }, [ready, api]);

  if (status.kind === 'auth-loading' || status.kind === 'loading') {
    return (
      <BackdropPage header={<BackdropHeader />}>
        <Box sx={{ display: 'grid', placeItems: 'center', pt: '40vh' }} role="status" aria-label="Loading your account">
          <CircularProgress sx={{ color: BRAND.parchment }} />
        </Box>
      </BackdropPage>
    );
  }

  if (status.kind === 'logged-out' || !user) {
    return <Navigate to={ACCOUNT_PATHS.logIn} replace state={{ returnTo: { path: ACCOUNT_ROOT } }} />;
  }

  // The API's copy, which the webhook keeps current; AuthKit's while it can't be reached.
  const me = status.kind === 'ready' ? status.me : null;
  const name = [me ? me.firstName : user.firstName, me ? me.lastName : user.lastName]
    .filter(Boolean)
    .join(' ');
  const email = me?.email ?? user.email;

  const held = recallOrigin(userId);
  const originKey = answers?.origin ?? held.origin;

  const endSession = () => {
    forgetOrigin();
    try {
      signOut({ returnTo: 'https://myclimateweek.com/' });
    } catch {
      // No session left to end: the page is logged out already.
      window.location.assign(import.meta.env.BASE_URL);
    }
  };

  const signMeOut = async () => {
    setLeaving(true);
    const saved = await flushBeforeSignOut();
    if (saved) {
      endSession();
      return;
    }
    setLeaving(false);
    setDialog('unsaved');
  };

  const deleteAccount = async () => {
    setDeleting(true);
    setDeleteFailed(false);
    try {
      await api.deleteMe();
      setDialog(null);
      endSession();
    } catch (err) {
      // 404 and 405 are the endpoint not being there yet; a 5xx is the same
      // from here. Either way the account is still open, so nobody is signed
      // out on the strength of a delete that did not happen.
      setDeleting(false);
      setDeleteFailed(true);
      if (!(err instanceof ApiError)) return;
    }
  };

  const editAt = (step: Step) => navigate(`${ACCOUNT_PATHS.welcome}?edit=1&step=${step}`);

  const group = (step: Step, lead: string, chips: { key: string; emoji: string; label: string }[], chipSize: number) => (
    <Group type="button" aria-label={EDIT_LABEL[step]} onClick={() => editAt(step)}>
      <Lead style={{ fontSize: chipSize + 1 }}>{lead}</Lead>
      <Chips>
        {chips.length > 0 ? (
          chips.map((chip) => (
            <Chip key={chip.key} style={{ fontSize: chipSize }}>
              <Emoji style={{ fontSize: chipSize + 2 }}>{chip.emoji}</Emoji>
              <b>{chip.label}</b>
            </Chip>
          ))
        ) : (
          /* An account made before the question existed has no answer for it.
             Hiding the group would leave it undiscoverable for exactly the
             people who never saw the question (Sam, Sept 19). */
          <AddAnswer style={{ fontSize: chipSize }}>+ Add an answer</AddAnswer>
        )}
      </Chips>
    </Group>
  );

  const groups = (chipSize: number) => (
    <>
      {group(
        'descriptors',
        COPY.account.descriptors,
        chipsFor(answers?.descriptors ?? [], DESCRIPTORS, answers?.descriptorOther ?? null),
        chipSize,
      )}
      {group(
        'origin',
        COPY.account.origin,
        originKey ? chipsFor([originKey], ORIGINS, null) : [],
        chipSize,
      )}
      {group(
        'reasons',
        COPY.account.reasons,
        chipsFor(answers?.reasons ?? [], REASONS, answers?.reasonOther ?? null),
        chipSize,
      )}
    </>
  );

  const card = (chipSize: number, nameSize: number, emailSize: number, padding: string) => (
    <Card style={{ padding }} data-desk={phone ? undefined : ''}>
      <Identity style={{ marginBottom: phone ? 11 : 14 }}>
        <AccountIcon size={phone ? 50 : 58} />
        <Name>
          <strong style={{ fontSize: nameSize }}>{name || 'My account'}</strong>
          <span style={{ fontSize: emailSize }}>{email}</span>
        </Name>
      </Identity>
      {groups(chipSize)}
    </Card>
  );

  const statsCard = week && (
    <Stats>
      <StatsTitle>Stats for my week:</StatsTitle>
      {WEEK_STAT_ROWS.map(({ key, label }) => (
        <StatRow key={key}>
          <span>{label}:</span>
          <strong style={{ color: isWarningStat(key) && week.stats[key] ? '#ef3d23' : undefined }}>
            {week.stats[key]}
          </strong>
        </StatRow>
      ))}
    </Stats>
  );

  const deleteTrouble = deleteFailed && (
    <Trouble role="alert">
      We couldn’t delete your account just now. Email{' '}
      <a href="mailto:info@commonground.boston">info@commonground.boston</a> and we’ll do it for you.
    </Trouble>
  );

  const body = phone ? (
    <PhoneColumn>
      {card(12.5, 20, 13, '11px 16px 8px')}

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <PhoneAction type="button" onClick={() => setDialog('link')}>
          <LinkGlyph size={26} />
          Share my
          <br />
          public link
        </PhoneAction>
        <PhoneAction type="button" onClick={() => setDialog('calendar')}>
          <CalendarSyncGlyph size={26} />
          Sync with
          <br />
          my calendar
        </PhoneAction>
      </Box>

      {statsCard}

      {deleteTrouble}

      {/* Below the mockup's fold, in Sam's order. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <WideAction
          type="button"
          onClick={() => setDialog('clear')}
          disabled={!week || week.counts.all === 0}
        >
          Clear my Schedule
        </WideAction>
        <WideAction type="button" onClick={signMeOut} disabled={leaving} aria-busy={leaving}>
          {leaving ? 'Signing out…' : 'Sign Out'}
        </WideAction>
        <WideAction type="button" data-danger="true" onClick={() => setDialog('delete')}>
          Delete My Account
        </WideAction>
      </Box>

      <LegalFooter />
    </PhoneColumn>
  ) : (
    <DeskColumns>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
        {card(15.5, 23, 15, '12px 22px 16px')}
        <LegalFooter />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '17px', minWidth: 0 }}>
        <DeskAction type="button" onClick={() => setDialog('link')}>
          <LinkGlyph size={25} />
          Share my public link
        </DeskAction>
        <DeskAction type="button" onClick={() => setDialog('calendar')}>
          <CalendarSyncGlyph size={25} />
          Sync with my calendar
        </DeskAction>

        <Box sx={{ mt: '22px', display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'flex-start' }}>
          <PlainAction type="button" onClick={signMeOut} disabled={leaving} aria-busy={leaving}>
            {leaving ? 'Signing out…' : 'Sign out'}
          </PlainAction>
          <PlainAction type="button" onClick={() => setDialog('delete')}>
            Delete my account
          </PlainAction>
        </Box>

        {deleteTrouble}
      </Box>
    </DeskColumns>
  );

  return (
    <BackdropPage header={<BackdropHeader />} bottom={phone ? TOGGLE_BAND : undefined}>
      {body}

      <ShareLinkDialog open={dialog === 'link'} onClose={() => setDialog(null)} />
      <CalendarSyncDialog open={dialog === 'calendar'} onClose={() => setDialog(null)} />

      {week && (
        <ClearScheduleDialog
          open={dialog === 'clear'}
          onClose={() => setDialog(null)}
          counts={week.counts}
          onClear={week.clear}
        />
      )}

      <Dialog
        open={dialog === 'delete'}
        onClose={() => (deleting ? null : setDialog(null))}
        aria-labelledby="delete-account-title"
        slotProps={{
          paper: { sx: { background: DIALOG_BG, backgroundImage: 'none', borderRadius: 3, p: 3, maxWidth: 400 } },
        }}
      >
        <Typography
          id="delete-account-title"
          sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, mb: 1, color: BRAND.parchment }}
        >
          Are you sure?
        </Typography>
        <Typography sx={{ fontSize: 13.5, opacity: 0.75, mb: 2.5, color: BRAND.parchment, lineHeight: 1.5 }}>
          This action cannot be undone. Your schedule, your saved events and your sign-up answers go with it.
        </Typography>
        {deleteFailed && (
          <Typography sx={{ fontSize: 13.5, mb: 2, color: BRAND.parchment, lineHeight: 1.5 }} role="alert">
            We couldn’t delete your account just now. Email{' '}
            <a href="mailto:info@commonground.boston" style={{ color: 'inherit' }}>
              info@commonground.boston
            </a>{' '}
            and we’ll do it for you.
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          {/* The destructive one is the quiet one, and not what focus lands on. */}
          <Button
            autoFocus
            onClick={() => setDialog(null)}
            sx={{
              background: BRAND.parchment,
              color: BRAND.mocha,
              fontWeight: 700,
              borderRadius: 999,
              px: 3,
              '&:hover': { background: '#fff' },
            }}
          >
            Keep my account
          </Button>
          <Button
            onClick={deleteAccount}
            disabled={deleting}
            sx={{ color: 'rgba(243,238,231,0.8)', fontWeight: 600 }}
          >
            {deleting ? 'Deleting…' : 'Delete my account'}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={dialog === 'unsaved'}
        onClose={() => setDialog(null)}
        aria-labelledby="unsaved-title"
        slotProps={{
          paper: { sx: { background: DIALOG_BG, backgroundImage: 'none', borderRadius: 3, p: 3, maxWidth: 380 } },
        }}
      >
        <Typography
          id="unsaved-title"
          sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, mb: 1, color: BRAND.parchment }}
        >
          Some changes haven&rsquo;t saved to your account yet. Sign out anyway?
        </Typography>
        <Typography sx={{ fontSize: 13.5, opacity: 0.7, mb: 2.5, color: BRAND.parchment, lineHeight: 1.5 }}>
          If you stay, they&rsquo;ll keep trying to save.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setDialog(null)} sx={{ color: 'rgba(243,238,231,0.8)', fontWeight: 600 }}>
            Stay signed in
          </Button>
          <Button
            onClick={endSession}
            sx={{
              background: BRAND.parchment,
              color: BRAND.mocha,
              fontWeight: 700,
              borderRadius: 999,
              px: 3,
              '&:hover': { background: '#fff' },
            }}
          >
            Sign out anyway
          </Button>
        </Box>
      </Dialog>
    </BackdropPage>
  );
}
